import { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import InputSearch from '/src/components/UI/InputSearch/InputSearch';
import ConversationCompletedService from '../../services/ConversationCompletedService';
import useUnitLabels from '/src/hooks/useUnitLabels';
import { buildCriteriaKey } from '/src/utils/criteriaKey';
import './ConversationFiltersBar.css';

const CRITERIA_CATEGORY_COLORS = [
    "#4c9aff", "#36b37e", "#ff8b00", "#c33b8f",
    "#00b8d9", "#6554c0", "#ff5630", "#8993a4"
];

const criteriaResultOptions = [
    { value: "pass", label: "Passed" },
    { value: "fail", label: "Failed" },
    { value: "empty", label: "Not evaluated" },
];

export const DEFAULT_CONVERSATION_FILTERS = {
    call_id: "",
    client_id: "",
    agent_id: "",
    unit_ids: [],
    criteria_ids: [],
    criteria_categories: [],
    criteria_results: [],
    date_start: "",
    date_end: "",
    score_min: 0,
    score_max: 100
};

function ConversationFiltersBar({ filters, setFilters }) {
    const { getCriteriasAll } = ConversationCompletedService;
    const { unitLabels } = useUnitLabels();
    const [criteriasByCategory, setCriteriasByCategory] = useState({});

    useEffect(() => {
        const fetchCriterias = async () => {
            const [response, error] = await getCriteriasAll();
            if (error || !response) {
                console.error("Failed to fetch criterias:", error);
                return;
            }
            setCriteriasByCategory(response);
        };

        fetchCriterias();
    }, []);

    const categoryColors = useMemo(() => {
        const categories = Object.keys(criteriasByCategory);
        return categories.reduce((acc, category, index) => {
            acc[category] = CRITERIA_CATEGORY_COLORS[index % CRITERIA_CATEGORY_COLORS.length];
            return acc;
        }, {});
    }, [criteriasByCategory]);

    const groupedCriteriaOptions = useMemo(
        () =>
            Object.entries(criteriasByCategory).map(([category, items]) => ({
                label: category,
                color: categoryColors[category],
                options: (items || []).map((criteria) => ({
                    value: buildCriteriaKey(category, criteria.id),
                    label: criteria.name,
                    category
                }))
            })),
        [criteriasByCategory, categoryColors]
    );

    const criteriaOptions = useMemo(
        () => groupedCriteriaOptions.flatMap((group) => group.options),
        [groupedCriteriaOptions]
    );

    const unitGroupColorByName = useMemo(() => {
        const names = [...new Set(unitLabels.map((u) => u.label.split(" ")[0]))];
        return Object.fromEntries(names.map((name, i) => [name, CRITERIA_CATEGORY_COLORS[i % CRITERIA_CATEGORY_COLORS.length]]));
    }, [unitLabels]);

    const groupedUnitOptions = useMemo(() => {
        const groups = {};
        unitLabels.forEach((u) => {
            const groupName = u.label.split(" ")[0];
            groups[groupName] = groups[groupName] || [];
            groups[groupName].push({ value: u.unit_id, label: u.label, group: groupName });
        });
        return Object.entries(groups).map(([groupName, options]) => ({
            label: groupName,
            color: unitGroupColorByName[groupName],
            options,
        }));
    }, [unitLabels, unitGroupColorByName]);

    const unitOptionsFlat = useMemo(() => groupedUnitOptions.flatMap((g) => g.options), [groupedUnitOptions]);

    return (
        <div className="conversation-filters-bar" style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <InputSearch name="call_id" placeholderLibelle="Search call" style={{ width: "210px", marginBottom: "10px" }}
                value={filters.call_id}
                onChange={(e) =>
                    setFilters(prev => ({ ...prev, call_id: e.target.value }))
                }
            />
            <InputSearch name="client_id" placeholderLibelle="Search client" style={{ width: "210px", marginBottom: "10px" }}
                value={filters.client_id}
                onChange={(e) =>
                    setFilters(prev => ({ ...prev, client_id: e.target.value }))
                }
            />
            <InputSearch name="agent_id" placeholderLibelle="Search agent" style={{ width: "210px", marginBottom: "10px" }}
                value={filters.agent_id}
                onChange={(e) =>
                    setFilters(prev => ({ ...prev, agent_id: e.target.value }))
                }
            />
            <Select
                classNamePrefix="criteria-select"
                isMulti
                options={groupedUnitOptions}
                value={unitOptionsFlat.filter(opt => filters.unit_ids.includes(opt.value))}
                getOptionValue={(opt) => opt.value}
                onChange={(selected) =>
                    setFilters(prev => ({ ...prev, unit_ids: selected ? selected.map(s => s.value) : [] }))
                }
                hideSelectedOptions={false}
                menuPortalTarget={document.body}
                formatGroupLabel={(group) => (
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: group.color }} />
                        <span>{group.label}</span>
                    </div>
                )}
                formatOptionLabel={(opt) => (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ width: "4px", alignSelf: "stretch", borderRadius: "2px", background: unitGroupColorByName[opt.group] }} />
                        <span>{opt.label}</span>
                    </div>
                )}
                styles={{
                    option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? "var(--secondary)" : base.backgroundColor,
                        color: state.isSelected ? "var(--main)" : base.color
                    }),
                    groupHeading: (base, state) => ({
                        ...base,
                        color: state.data.color,
                        fontWeight: 600,
                        fontSize: "0.85em",
                        textTransform: "none"
                    }),
                    container: (base) => ({ ...base, width: "210px", marginBottom: "10px" }),
                    menu: (base) => ({ ...base, marginTop: -10 }),
                    menuList: (base) => ({ ...base, overscrollBehavior: "contain" }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
                controlShouldRenderValue={false}
                closeMenuOnSelect={false}
                placeholder={
                    filters.unit_ids.length
                        ? `${filters.unit_ids.length} unit${filters.unit_ids.length > 1 ? "s" : ""} selected`
                        : "Filter by unit..."
                }
            />
            <Tippy
                className="date-input-tooltip"
                content={<div className="date-input-text-tooltip">Scoring range</div>}
                zIndex={9999}
                placement="top"
                delay={[200, 0]}
            >
                <div>
                    <div className="container_scoring">
                        <div className="min_slider">Min: <b>{filters.score_min}</b></div>
                        <div className="max_slider">Max: <b>{filters.score_max}</b></div>
                    </div>
                    <div className="slider_container">
                        <Slider
                            className="score-slider"
                            range
                            min={0}
                            max={100}
                            value={[filters.score_min, filters.score_max]}
                            onChange={([min, max]) =>
                                setFilters(prev => ({ ...prev, score_min: min, score_max: max }))
                            }
                        />
                    </div>
                </div>
            </Tippy>

            <div className="container_dates">
                <Tippy
                    className="date-input-tooltip"
                    content={<div className="date-input-text-tooltip">Date Start</div>}
                    placement="top"
                    delay={[200, 0]}
                >
                    <input
                        type="date"
                        className="input_search"
                        value={filters.date_start}
                        onChange={(e) =>
                            setFilters(prev => ({ ...prev, date_start: e.target.value }))
                        }
                    />
                </Tippy>

                <Tippy
                    className="date-input-tooltip"
                    content={<div className="date-input-text-tooltip">Date End</div>}
                    placement="top"
                    delay={[200, 0]}
                >
                    <input
                        type="date"
                        className="input_search"
                        value={filters.date_end}
                        onChange={(e) =>
                            setFilters(prev => ({ ...prev, date_end: e.target.value }))
                        }
                    />
                </Tippy>
            </div>

            <Select
                classNamePrefix="criteria-select"
                isMulti
                options={groupedCriteriaOptions}
                value={criteriaOptions.filter(opt => filters.criteria_ids.includes(opt.value))}
                getOptionValue={(opt) => opt.value}
                onChange={(selected) =>
                    setFilters(prev => ({
                        ...prev,
                        criteria_ids: selected ? selected.map(s => s.value) : [],
                        criteria_categories: selected ? [...new Set(selected.map(s => s.category))] : []
                    }))
                }
                hideSelectedOptions={false}
                formatGroupLabel={(group) => (
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: group.color }} />
                        <span>{group.label}</span>
                    </div>
                )}
                formatOptionLabel={(opt) => (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ width: "4px", alignSelf: "stretch", borderRadius: "2px", background: categoryColors[opt.category] }} />
                        <span>{opt.label}</span>
                    </div>
                )}
                styles={{
                    option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? "var(--secondary)" : base.backgroundColor,
                        color: state.isSelected ? "var(--main)" : base.color
                    }),
                    groupHeading: (base, state) => ({
                        ...base,
                        color: state.data.color,
                        fontWeight: 600,
                        fontSize: "0.85em",
                        textTransform: "none"
                    }),
                    container: (base) => ({ ...base, width: "350px", marginBottom: "10px", zIndex: 2 }),
                    menu: (base) => ({ ...base, marginTop: -10 }),
                }}
                controlShouldRenderValue={false}
                closeMenuOnSelect={false}
                placeholder={
                    filters.criteria_ids.length
                        ? `${filters.criteria_ids.length} criteria selected`
                        : "Filter by criteria..."
                }
            />
            <Select
                classNamePrefix="criteria-select"
                isMulti
                options={criteriaResultOptions}
                value={criteriaResultOptions.filter(opt => filters.criteria_results.includes(opt.value))}
                getOptionValue={(opt) => opt.value}
                onChange={(selected) =>
                    setFilters(prev => ({ ...prev, criteria_results: selected ? selected.map(s => s.value) : [] }))
                }
                hideSelectedOptions={false}
                styles={{
                    option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? "var(--secondary)" : base.backgroundColor,
                        color: state.isSelected ? "var(--main)" : base.color
                    }),
                    container: (base) => ({ ...base, width: "350px", marginBottom: "10px", zIndex: 2 }),
                    menu: (base) => ({ ...base, marginTop: -10 }),
                }}
                controlShouldRenderValue={false}
                closeMenuOnSelect={false}
                placeholder={
                    filters.criteria_results.length
                        ? `${filters.criteria_results.length} type${filters.criteria_results.length > 1 ? "s" : ""} selected`
                        : "Filter by type..."
                }
            />
        </div>
    );
}

export default ConversationFiltersBar;
